/**
 * Fabrication Manager
 * 
 * Acts as the "Database" for MEP Fabrication Parts.
 * Manages Configuration, Services, and Connectivity Logic.
 */

import { v4 as uuidv4 } from 'uuid';
import {
    FabricationService,
    FabricationPartElement,
    FabricationPartGeometryData,
    FabricationConnector
} from './types';

export class FabricationManager {
    private services: Map<string, FabricationService> = new Map();
    private partCatalog: Map<number, any> = new Map(); // CID -> Part Definition

    constructor() {
        this.loadMockConfiguration();
    }

    private loadMockConfiguration() {
        // 1. Define Services
        const serviceHVAC: FabricationService = {
            name: 'Supply Air 2in WG',
            group: 'HVAC',
            parts: [
                { cid: 1, name: 'Rect Duct', type: 'Straight' },
                { cid: 2, name: 'Rect Elbow', type: 'Elbow' },
                { cid: 3, name: 'Rect Tap', type: 'Tap' }
            ]
        };

        const servicePiping: FabricationService = {
            name: 'Chilled Water Supply',
            group: 'Piping',
            parts: [
                { cid: 101, name: 'Steel Pipe', type: 'Straight' },
                { cid: 102, name: 'Steel Elbow 90', type: 'Elbow' },
                { cid: 103, name: 'Steel Tee', type: 'Tee' }
            ]
        };

        this.services.set(serviceHVAC.name, serviceHVAC);
        this.services.set(servicePiping.name, servicePiping);

        // 2. Define Catalog (Mock Data)
        this.partCatalog.set(1, {
            name: 'Rect Duct',
            defaultLength: 1.5, // 1.5m standard coil
            connectors: [
                { type: 'Flanged', gender: 'Neutral' },
                { type: 'Flanged', gender: 'Neutral' }
            ]
        });

        this.partCatalog.set(2, {
            name: 'Rect Elbow',
            isFitting: true,
            connectors: [
                { type: 'Flanged', gender: 'Neutral' },
                { type: 'Flanged', gender: 'Neutral' }
            ]
        });

        this.partCatalog.set(101, {
            name: 'Steel Pipe',
            defaultLength: 6.0, // 6m standard pipe
            connectors: [
                { type: 'Bevell', gender: 'Neutral' },
                { type: 'Bevell', gender: 'Neutral' }
            ]
        });

        this.partCatalog.set(102, {
            name: 'Steel Elbow 90',
            isFitting: true,
            connectors: [
                { type: 'Bevell', gender: 'Neutral' },
                { type: 'Bevell', gender: 'Neutral' }
            ]
        });
    }

    public getService(name: string): FabricationService | undefined {
        return this.services.get(name);
    }

    public getAllServices(): FabricationService[] {
        return Array.from(this.services.values());
    }

    public getPartDefinition(cid: number): any {
        return this.partCatalog.get(cid);
    }

    /**
     * Creates a Fabrication Part Element instance from a CID.
     */
    public createPart(cid: number, serviceName: string, position: [number, number, number]): FabricationPartElement | null {
        const def = this.partCatalog.get(cid);
        if (!def) return null;

        // Generate connectors instances
        const connectors: FabricationConnector[] = def.connectors.map((c: any) => ({
            id: uuidv4(),
            position: [0, 0, 0], // Needs geometry logic to place correctly
            direction: [1, 0, 0],
            systemType: 'Supply Air', // Default, should be passed
            size: 300,
            shape: 'Rectangular',
            connectorType: c.type,
            gender: c.gender
        }));

        const geometryData: FabricationPartGeometryData = {
            position,
            rotation: [0, 0, 0],
            cid,
            connectors
        };

        return {
            id: uuidv4(),
            category: 'Fabrication Parts',
            serviceName,
            partCid: cid,
            type: def.name,
            parameters: {
                'Service': serviceName,
                'CID': cid.toString(),
                'Item No': '1'
            },
            constraints: [],
            geometryData
        };
    }

    /**
     * Checks if two connectors are compatible for fabrication.
     */
    public areConnectorsCompatible(c1: FabricationConnector, c2: FabricationConnector): boolean {
        // 1. Shape Match
        if (c1.shape !== c2.shape) return false;

        // 2. Type Match (e.g. Flange to Flange)
        if (c1.connectorType !== c2.connectorType) return false;

        // 3. Gender Logic (Male<->Female, Neutral<->Neutral)
        if (c1.gender === 'Neutral' && c2.gender === 'Neutral') return true;
        if (c1.gender === 'Male' && c2.gender === 'Female') return true;
        if (c1.gender === 'Female' && c2.gender === 'Male') return true;

        return false;
    }
}
